import { createClient, type SupabaseClient, type User } from '@supabase/supabase-js'
import type { AppState, CollectionName, QueuedMutation, RemoteRecord, SerializedRecord, SyncConflict } from '../types'
import { ALL_COLLECTIONS, ARRAY_COLLECTIONS, CONFIG_KEY, LEGACY_META_KEY, META_KEY, SINGLE_COLLECTIONS } from '../domain/constants'
import { initialState } from '../domain/seed'
import { normalizeState } from '../domain/validation'
import { logger } from '../logging/logger'
import { readJson, recordRevision, writeJson } from '../storage/localStore'

type SyncStatus = 'local' | 'signed-out' | 'syncing' | 'synced' | 'offline' | 'error' | 'conflict'

interface RuntimeConfig {
  supabaseUrl: string
  supabasePublishableKey: string
  appUrl: string
  workspaceName: string
}

interface SyncMeta {
  deviceId: string
  deviceName: string
  queue: Record<string, QueuedMutation>
  snapshot: Record<string, SerializedRecord & { client_updated_at?: string | null }>
  conflicts: Record<string, SyncConflict>
  retryCount: number
  lastSyncAt: string | null
  lastPullAt: string | null
  lastError: string | null
}

interface SyncHooks {
  getState: () => AppState
  applyRemoteState: (state: AppState) => void
  onStatus: () => void
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T
const stable = (value: unknown): string => JSON.stringify(value)
const nowIso = (): string => new Date().toISOString()

const defaultConfig = (): RuntimeConfig => {
  const fileConfig = window.TREVAHHH_SYNC_CONFIG ?? {}
  const localConfig = readJson<Partial<RuntimeConfig>>(CONFIG_KEY, {})
  return {
    supabaseUrl: localConfig.supabaseUrl || fileConfig.supabaseUrl || '',
    supabasePublishableKey: localConfig.supabasePublishableKey || fileConfig.supabasePublishableKey || '',
    appUrl: localConfig.appUrl || fileConfig.appUrl || currentAppUrl(),
    workspaceName: fileConfig.workspaceName || 'Jay Carr Creative Empire'
  }
}

function currentAppUrl(): string {
  if (location.protocol === 'http:' || location.protocol === 'https:') return `${location.origin}${location.pathname}`
  return ''
}

function defaultDeviceName(): string {
  const ua = navigator.userAgent
  if (/iPhone|iPad/i.test(ua)) return 'iPhone / iPad'
  if (/Android/i.test(ua)) return 'Android device'
  if (/Macintosh|Mac OS/i.test(ua)) return 'Mac'
  if (/Windows/i.test(ua)) return 'Windows PC'
  return 'Web device'
}

function createDeviceId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function loadMeta(): SyncMeta {
  const legacy = readJson<Partial<SyncMeta>>(LEGACY_META_KEY, {})
  const current = readJson<Partial<SyncMeta>>(META_KEY, legacy)
  return {
    deviceId: current.deviceId || createDeviceId(),
    deviceName: current.deviceName || defaultDeviceName(),
    queue: current.queue || {},
    snapshot: current.snapshot || {},
    conflicts: current.conflicts || {},
    retryCount: current.retryCount || 0,
    lastSyncAt: current.lastSyncAt || null,
    lastPullAt: current.lastPullAt || null,
    lastError: current.lastError || null
  }
}

export class TrevahhhSyncEngine {
  private client: SupabaseClient | null = null
  private user: User | null = null
  private recordChannel: ReturnType<SupabaseClient['channel']> | null = null
  private presenceChannel: ReturnType<SupabaseClient['channel']> | null = null
  private pullTimer: number | null = null
  private flushPromise: Promise<void> | null = null
  private applyingRemote = false
  public initialized = false
  public onlineDevices = 1
  public status: SyncStatus = 'local'
  public statusDetail = 'Local-only mode'
  public meta: SyncMeta = loadMeta()

  constructor(private hooks: SyncHooks) {
    this.persistMeta()
    window.addEventListener('online', () => void this.handleOnline())
    window.addEventListener('offline', () => this.updateStatus('offline', 'Offline - changes are queued'))
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.user && navigator.onLine) void this.pullRemote()
    })
  }

  get configured(): boolean {
    const config = this.config()
    return /^https:\/\/.+\.supabase\.co$/i.test(config.supabaseUrl) && config.supabasePublishableKey.length > 20 && !/service_role|sb_secret/i.test(config.supabasePublishableKey)
  }

  config(): RuntimeConfig {
    return defaultConfig()
  }

  get currentUser(): User | null {
    return this.user
  }

  get supabaseClient(): SupabaseClient | null {
    return this.client
  }

  get queueSize(): number {
    return Object.keys(this.meta.queue).length
  }

  get conflicts(): SyncConflict[] {
    return Object.values(this.meta.conflicts)
  }

  persistMeta(): void {
    writeJson(META_KEY, this.meta)
  }

  async init(): Promise<void> {
    if (!Object.keys(this.meta.snapshot).length) {
      this.meta.snapshot = this.serializeState(this.hooks.getState())
      this.persistMeta()
    }
    if (!this.configured) {
      this.updateStatus('local', 'Cloud sync needs safe public Supabase configuration')
      this.initialized = true
      return
    }
    await this.createClientAndStart()
    this.initialized = true
  }

  private async createClientAndStart(): Promise<void> {
    const config = this.config()
    this.client = createClient(config.supabaseUrl, config.supabasePublishableKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'trevahhh-supabase-auth'
      },
      realtime: { params: { eventsPerSecond: 8 } }
    })
    const { data, error } = await this.client.auth.getSession()
    if (error) this.updateStatus('error', error.message)
    this.user = data.session?.user ?? null
    this.client.auth.onAuthStateChange((_event, session) => {
      const changedUser = session?.user?.id !== this.user?.id
      this.user = session?.user ?? null
      if (this.user && changedUser) void this.startAuthenticatedSync()
      if (!this.user) void this.stopRealtime()
      this.hooks.onStatus()
    })
    if (this.user) await this.startAuthenticatedSync()
    else this.updateStatus('signed-out', 'Sign in to sync every device')
  }

  private async startAuthenticatedSync(): Promise<void> {
    this.updateStatus('syncing', 'Connecting to your workspace')
    await this.stopRealtime()
    await this.pullRemote()
    await this.flush()
    await this.subscribeRealtime()
    this.updateStatus(navigator.onLine ? 'synced' : 'offline', navigator.onLine ? 'Synced across devices' : 'Offline - changes are queued')
  }

  private async subscribeRealtime(): Promise<void> {
    if (!this.client || !this.user) return
    this.recordChannel = this.client.channel(`trevahhh-records-${this.user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workspace_records', filter: `owner_id=eq.${this.user.id}` }, (payload) => {
        const row = payload.new as { device_id?: string } | null
        if (row?.device_id === this.meta.deviceId) return
        if (this.pullTimer) window.clearTimeout(this.pullTimer)
        this.pullTimer = window.setTimeout(() => void this.pullRemote(), 180)
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') this.updateStatus('error', 'Realtime connection failed; manual sync remains available')
      })

    this.presenceChannel = this.client.channel(`trevahhh-presence-${this.user.id}`, { config: { presence: { key: this.meta.deviceId } } })
      .on('presence', { event: 'sync' }, () => {
        const presence = this.presenceChannel?.presenceState() ?? {}
        this.onlineDevices = Math.max(1, Object.keys(presence).length)
        this.hooks.onStatus()
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await this.presenceChannel?.track({
            deviceName: this.meta.deviceName,
            onlineAt: nowIso(),
            appVersion: '1.1.0'
          })
        }
      })
  }

  private async stopRealtime(): Promise<void> {
    if (this.recordChannel && this.client) await this.client.removeChannel(this.recordChannel).catch(() => undefined)
    if (this.presenceChannel && this.client) await this.client.removeChannel(this.presenceChannel).catch(() => undefined)
    this.recordChannel = null
    this.presenceChannel = null
    this.onlineDevices = 1
  }

  serializeState(state: AppState): Record<string, SerializedRecord & { client_updated_at?: string | null }> {
    const map: Record<string, SerializedRecord & { client_updated_at?: string | null }> = {}
    for (const collection of ARRAY_COLLECTIONS) {
      const items = Array.isArray(state[collection]) ? state[collection] : []
      items.forEach((item, index) => {
        const objectItem = item as unknown as Record<string, unknown>
        const recordId = String(objectItem.id ?? objectItem.day ?? `${collection}-${index}`)
        const payload = { ...clone(objectItem), __order: index }
        map[`${collection}:${recordId}`] = { collection, recordId, payload }
      })
    }
    for (const collection of SINGLE_COLLECTIONS) {
      map[`${collection}:singleton`] = { collection, recordId: 'singleton', payload: clone(state[collection] as unknown as Record<string, unknown>) }
    }
    return map
  }

  reconstructState(recordMap: Record<string, RemoteRecord>): AppState {
    const base = initialState()
    for (const collection of ARRAY_COLLECTIONS) base[collection] = [] as never
    const grouped: Partial<Record<CollectionName, Record<string, unknown>[]>> = {}
    for (const collection of ARRAY_COLLECTIONS) grouped[collection] = []
    for (const record of Object.values(recordMap)) {
      if (!record || record.deleted || !ALL_COLLECTIONS.includes(record.collection)) continue
      const payload = clone(record.payload)
      if (ARRAY_COLLECTIONS.includes(record.collection as typeof ARRAY_COLLECTIONS[number])) grouped[record.collection]?.push(payload)
      else (base as unknown as Record<string, unknown>)[record.collection] = payload
    }
    for (const collection of ARRAY_COLLECTIONS) {
      const sorted = (grouped[collection] ?? []).sort((a, b) => Number(a.__order ?? 999999) - Number(b.__order ?? 999999))
      ;(base as unknown as Record<string, unknown>)[collection] = sorted.map((item) => {
        delete item.__order
        return item
      })
    }
    return normalizeState(base)
  }

  onLocalStateChanged(nextState: AppState): void {
    if (this.applyingRemote) return
    const current = this.serializeState(nextState)
    const previous = this.meta.snapshot || {}
    const timestamp = nowIso()

    for (const [key, record] of Object.entries(current)) {
      const before = previous[key]
      if (!before || stable(before.payload) !== stable(record.payload)) {
        this.meta.queue[key] = {
          collection: record.collection,
          record_id: record.recordId,
          payload: record.payload,
          deleted: false,
          client_updated_at: timestamp,
          base_client_updated_at: before?.client_updated_at ?? null,
          device_id: this.meta.deviceId
        }
        recordRevision({ key, collection: record.collection, recordId: record.recordId, operation: 'local-change', deviceId: this.meta.deviceId, payload: record.payload })
      }
    }
    for (const [key, record] of Object.entries(previous)) {
      if (!current[key]) {
        this.meta.queue[key] = {
          collection: record.collection,
          record_id: record.recordId,
          payload: {},
          deleted: true,
          client_updated_at: timestamp,
          base_client_updated_at: record.client_updated_at ?? null,
          device_id: this.meta.deviceId
        }
      }
    }
    this.meta.snapshot = current
    this.persistMeta()
    if (this.user && navigator.onLine) void this.flush()
    else if (!navigator.onLine) this.updateStatus('offline', `${this.queueSize} change(s) queued offline`)
    this.hooks.onStatus()
  }

  queueEntireState(state: AppState): void {
    const records = this.serializeState(state)
    const timestamp = nowIso()
    for (const [key, record] of Object.entries(records)) {
      this.meta.queue[key] = {
        collection: record.collection,
        record_id: record.recordId,
        payload: record.payload,
        deleted: false,
        client_updated_at: timestamp,
        base_client_updated_at: record.client_updated_at ?? null,
        device_id: this.meta.deviceId
      }
    }
    this.meta.snapshot = records
    this.persistMeta()
  }

  async flush(): Promise<void> {
    if (this.flushPromise) return this.flushPromise
    this.flushPromise = this.performFlush().finally(() => {
      this.flushPromise = null
    })
    return this.flushPromise
  }

  private async performFlush(): Promise<void> {
    if (!this.client || !this.user || !navigator.onLine) return
    if (!this.queueSize) return
    await this.detectConflicts()
    if (this.conflicts.length) {
      this.updateStatus('conflict', `${this.conflicts.length} conflict(s) need review`)
      return
    }

    let entries = Object.entries(this.meta.queue)
    while (entries.length) {
      this.updateStatus('syncing', `Uploading ${entries.length} change(s)`)
      const batchEntries = entries.slice(0, 100)
      const batch = batchEntries.map(([, mutation]) => ({
        collection: mutation.collection,
        record_id: mutation.record_id,
        payload: mutation.payload,
        deleted: mutation.deleted,
        client_updated_at: mutation.client_updated_at,
        device_id: mutation.device_id
      }))
      const { data, error } = await this.client.rpc('apply_workspace_mutations', { p_mutations: batch })
      if (error) {
        this.meta.retryCount += 1
        this.meta.lastError = error.message
        this.persistMeta()
        this.updateStatus('error', `Sync failed: ${error.message}`)
        logger.error('Supabase mutation upload failed', { message: error.message })
        throw error
      }
      const appliedCount = typeof data === 'number' ? data : batchEntries.length
      if (appliedCount < batchEntries.length) {
        await this.detectConflicts()
        if (this.conflicts.length) {
          this.updateStatus('conflict', `${this.conflicts.length} conflict(s) need review`)
          return
        }
      }
      for (const [key, sent] of batchEntries) {
        if (this.meta.queue[key]?.client_updated_at === sent.client_updated_at) delete this.meta.queue[key]
      }
      this.meta.lastSyncAt = nowIso()
      this.meta.retryCount = 0
      this.meta.lastError = null
      this.persistMeta()
      entries = Object.entries(this.meta.queue)
    }
    this.updateStatus('synced', 'All changes synced')
  }

  async pullRemote(): Promise<void> {
    if (!this.client || !this.user || !navigator.onLine) return
    this.updateStatus('syncing', 'Checking for changes')
    const remote = await this.fetchRemoteRecords()
    if (!remote) return
    if (!Object.keys(remote).length) {
      this.queueEntireState(this.hooks.getState())
      await this.flush()
      this.meta.lastPullAt = nowIso()
      this.persistMeta()
      this.updateStatus('synced', 'First device initialized the cloud workspace')
      return
    }

    await this.detectConflicts(remote)
    if (this.conflicts.length) {
      this.updateStatus('conflict', `${this.conflicts.length} conflict(s) need review`)
      return
    }

    for (const [key, mutation] of Object.entries(this.meta.queue)) {
      const existing = remote[key]
      if (!existing || new Date(mutation.client_updated_at) >= new Date(existing.client_updated_at)) {
        remote[key] = {
          collection: mutation.collection,
          recordId: mutation.record_id,
          payload: mutation.payload,
          deleted: mutation.deleted,
          client_updated_at: mutation.client_updated_at,
          device_id: mutation.device_id
        }
      }
    }

    const merged = this.reconstructState(remote)
    this.applyingRemote = true
    try {
      this.hooks.applyRemoteState(merged)
      for (const [key, record] of Object.entries(remote)) {
        recordRevision({ key, collection: record.collection, recordId: record.recordId, operation: 'remote-apply', deviceId: this.meta.deviceId, payload: record.payload })
      }
    } finally {
      this.applyingRemote = false
    }
    this.meta.snapshot = this.serializeState(merged)
    for (const [key, record] of Object.entries(remote)) {
      if (this.meta.snapshot[key]) this.meta.snapshot[key].client_updated_at = record.client_updated_at
    }
    this.meta.lastPullAt = nowIso()
    this.persistMeta()
    if (this.queueSize) await this.flush()
    this.updateStatus('synced', 'Latest changes received')
  }

  private async fetchRemoteRecords(): Promise<Record<string, RemoteRecord> | null> {
    if (!this.client) return null
    const { data, error } = await this.client
      .from('workspace_records')
      .select('collection,record_id,payload,deleted,client_updated_at,device_id,updated_at')
      .order('updated_at', { ascending: true })
    if (error) {
      this.updateStatus('error', `Cloud read failed: ${error.message}`)
      logger.error('Supabase read failed', { message: error.message })
      return null
    }
    const records: Record<string, RemoteRecord> = {}
    for (const row of data ?? []) {
      const collection = row.collection as CollectionName
      if (!ALL_COLLECTIONS.includes(collection)) continue
      const key = `${collection}:${row.record_id}`
      records[key] = {
        collection,
        recordId: String(row.record_id),
        payload: row.payload as Record<string, unknown>,
        deleted: Boolean(row.deleted),
        client_updated_at: String(row.client_updated_at),
        device_id: String(row.device_id),
        updated_at: String(row.updated_at)
      }
    }
    return records
  }

  private async detectConflicts(remoteRecords?: Record<string, RemoteRecord>): Promise<void> {
    const remote = remoteRecords ?? await this.fetchRemoteRecords()
    if (!remote) return
    for (const [key, mutation] of Object.entries(this.meta.queue)) {
      const remoteRecord = remote[key]
      if (!remoteRecord || remoteRecord.device_id === this.meta.deviceId) continue
      const base = mutation.base_client_updated_at
      const remoteNewerThanBase = !base || new Date(remoteRecord.client_updated_at) > new Date(base)
      const payloadDiffers = stable(remoteRecord.payload) !== stable(mutation.payload) || remoteRecord.deleted !== mutation.deleted
      if (remoteNewerThanBase && payloadDiffers) {
        const conflict = {
          key,
          collection: mutation.collection,
          recordId: mutation.record_id,
          local: mutation,
          remote: remoteRecord,
          detectedAt: nowIso()
        }
        this.meta.conflicts[key] = conflict
        await this.recordCloudConflict(conflict)
      }
    }
    this.persistMeta()
  }

  private async recordCloudConflict(conflict: SyncConflict): Promise<void> {
    if (!this.client || !this.user) return
    const { error } = await this.client.from('workspace_conflicts').insert({
      owner_id: this.user.id,
      collection: conflict.collection,
      record_id: conflict.recordId,
      device_id: this.meta.deviceId,
      local_payload: conflict.local.payload,
      remote_payload: conflict.remote.payload,
      local_client_updated_at: conflict.local.client_updated_at,
      remote_client_updated_at: conflict.remote.client_updated_at,
      status: 'open',
      detected_at: conflict.detectedAt
    })
    if (error && error.code !== '23505') {
      logger.warn('Cloud conflict audit insert failed', { message: error.message })
    }
  }

  private async resolveCloudConflict(conflict: SyncConflict, resolution: 'local' | 'remote'): Promise<void> {
    if (!this.client || !this.user) return
    const { error } = await this.client
      .from('workspace_conflicts')
      .update({ status: 'resolved', resolution, resolved_at: nowIso() })
      .eq('owner_id', this.user.id)
      .eq('collection', conflict.collection)
      .eq('record_id', conflict.recordId)
      .eq('device_id', this.meta.deviceId)
      .eq('status', 'open')
    if (error) logger.warn('Cloud conflict audit resolution failed', { message: error.message })
  }

  async resolveConflict(key: string, choice: 'local' | 'remote'): Promise<void> {
    const conflict = this.meta.conflicts[key]
    if (!conflict) return
    if (choice === 'remote') {
      delete this.meta.queue[key]
      recordRevision({ key, collection: conflict.collection, recordId: conflict.recordId, operation: 'conflict-remote', deviceId: this.meta.deviceId, payload: conflict.remote.payload })
    } else {
      this.meta.queue[key] = {
        ...conflict.local,
        base_client_updated_at: conflict.remote.client_updated_at,
        client_updated_at: nowIso()
      }
      recordRevision({ key, collection: conflict.collection, recordId: conflict.recordId, operation: 'conflict-local', deviceId: this.meta.deviceId, payload: conflict.local.payload })
    }
    delete this.meta.conflicts[key]
    await this.resolveCloudConflict(conflict, choice)
    this.persistMeta()
    if (choice === 'remote') await this.pullRemote()
    else await this.flush()
    this.hooks.onStatus()
  }

  async handleOnline(): Promise<void> {
    if (!this.user) {
      this.updateStatus('signed-out', 'Online - sign in to sync')
      return
    }
    this.updateStatus('syncing', 'Back online - syncing queued work')
    await this.flush().catch(() => undefined)
    await this.pullRemote().catch(() => undefined)
  }

  updateStatus(status: SyncStatus, detail: string): void {
    this.status = status
    this.statusDetail = detail
    this.hooks.onStatus()
  }

  async saveConfiguration(config: RuntimeConfig): Promise<void> {
    if (!/^https:\/\/.+\.supabase\.co$/i.test(config.supabaseUrl)) throw new Error('Enter a valid Supabase project URL.')
    if (!config.supabasePublishableKey || /service_role|sb_secret/i.test(config.supabasePublishableKey)) throw new Error('Use only the public Supabase publishable key.')
    writeJson(CONFIG_KEY, config)
    await this.createClientAndStart()
  }

  clearConfiguration(): void {
    localStorage.removeItem(CONFIG_KEY)
    this.updateStatus('local', 'Supabase configuration removed from this device')
  }

  async sendMagicLink(email: string): Promise<void> {
    if (!this.client) throw new Error('Supabase client is not ready.')
    const redirectTo = this.config().appUrl || currentAppUrl()
    if (!redirectTo) throw new Error('Deploy the app to HTTPS before magic-link sign-in.')
    const { error } = await this.client.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo } })
    if (error) throw error
  }

  async passwordSignIn(email: string, password: string): Promise<void> {
    if (!this.client) throw new Error('Supabase client is not ready.')
    const { error } = await this.client.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  async passwordSignUp(email: string, password: string): Promise<void> {
    if (!this.client) throw new Error('Supabase client is not ready.')
    const redirectTo = this.config().appUrl || currentAppUrl()
    const { error } = await this.client.auth.signUp({ email, password, options: { emailRedirectTo: redirectTo || undefined } })
    if (error) throw error
  }

  async signOut(): Promise<void> {
    await this.client?.auth.signOut()
    this.user = null
    await this.stopRealtime()
    this.updateStatus('signed-out', 'Signed out - local changes remain queued on this device')
  }

  saveDeviceName(name: string): void {
    this.meta.deviceName = name.trim().slice(0, 80) || this.meta.deviceName
    this.persistMeta()
    void this.subscribeRealtime()
  }
}

declare global {
  interface Window {
    TREVAHHH_SYNC_CONFIG?: Partial<RuntimeConfig>
  }
}
